import { StorageKeys } from 'common/storage/constants';
import { DateString } from 'common/UrlDate';
import React, { useCallback, useEffect, useState } from 'react';
import {
  DishFirstSegmentFilter,
  HierarchicalAvailableCuisineCategoryFragment,
  NumberAvailableDishesResult,
  TagFilter,
} from 'src/gqlReactTypings.generated.d';
import { CONSUMER_HEADER_HEIGHT } from 'src/pages/consumer/components/header-v2/styles';
import { DishFirstExploreWidthLimit } from 'src/pages/consumer/dish-first/explore/components/SharedStyles';
import { UseExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { TopShefFilterButton, TopShefFilterButtonDivider } from 'src/top-shef/TopShefFilterButton';
import { TopShefInfoModal } from 'src/top-shef/TopShefInfoModal';
import styled, { css } from 'styled-components';
import { ifProp } from 'web-common/src/shared/styled-utils/ifProp';
import { Colors, Mixins, Spacing, Transition, ZIndex } from 'web-common/src/shared/styles';
import { BrowseByMealType } from './BrowseBy';
import { ClearFiltersButton } from './ExploreFilterButtons/ClearFiltersButton';
import { CuisineFilterButton } from './ExploreFilterButtons/CuisineFilterButton';
import { DateFilterButton } from './ExploreFilterButtons/DateFilterButton';
import { DietaryFilterButton } from './ExploreFilterButtons/DietaryFilterButton';
import { MoreFiltersButton } from './ExploreFilterButtons/MoreFiltersButton';
import { SubCuisineFilterButton } from './ExploreFilterButtons/SubCuisineFilterButton';
import { CuisineFilterCarousel } from './ExploreFilterModals/CuisineFilterCarousel';

export enum ExploreFilterIds {
  DatePicker = 'DatePicker',
  CuisineCarousel = 'CuisineCarousel',
  FilterButtonRow = 'FilterButtonRow',
}

const StyledExploreFilters = styled(DishFirstExploreWidthLimit)<{
  showFiltersInline: boolean;
  isMobileFiltersShowing?: boolean;
}>`
  ${({ isMobileFiltersShowing, showFiltersInline }) =>
    showFiltersInline
      ? css`
          padding: ${Spacing.SINGLE} ${Spacing.SINGLE} 0 ${Spacing.SINGLE};
          margin-top: ${isMobileFiltersShowing ? Spacing.DOUBLE_HALF : 0};
          background: ${Colors.WHITE};
          z-index: ${ZIndex.SegmentFiltersContainer};
          top: ${CONSUMER_HEADER_HEIGHT - 1}px; // subtract 1 to keep divs together
          transition: box-shadow ${Transition.Time.HALF} ease-in-out;

          ${Mixins.Media.minWidthSmall(`
          padding: ${Spacing.NONE} ${Spacing.SINGLE} ${Spacing.SINGLE_HALF};
          margin-top: ${Spacing.SINGLE_HALF};
          margin-bottom: ${Spacing.SINGLE};
          position: relative;
          top: unset;
        `)}
        `
      : ''}
`;

const ExploreFiltersContainer = styled.div<{ showInline: boolean }>`
  display: flex;
  flex-direction: column;
  ${ifProp(
    'showInline',
    css`
      flex-direction: row;
      align-items: center;
      gap: ${Spacing.HALF};

      ${Mixins.Media.maxWidthLarge(css`
        align-items: flex-start;
        flex-direction: column;
        overflow: auto;
        width: 100%;
      `)}
    `
  )}
`;

interface ExploreFiltersProps {
  loading: boolean;
  availableDeliveryDays: NumberAvailableDishesResult[];
  timeZone: string;
  allCuisineCategories?: HierarchicalAvailableCuisineCategoryFragment[];
  availableCuisineCategories: HierarchicalAvailableCuisineCategoryFragment[];
  onDateChange: (date?: DateString) => void;
  overflowDishFilters?: boolean;
  filters: UseExploreUrl<DishFirstSegmentFilter>;
  showCuisineFilterAsDropdown?: boolean;
  /**
   * If true, the filter section will be shown in-line.
   * However, filter buttons may not necessarily be shown in-line
   * as well (see `showFilterButtonsInline`).
   */
  showFiltersInline?: boolean;
  totalShefs?: number;
}

export const ExploreFilters: React.FC<ExploreFiltersProps> = React.memo(
  ({
    allCuisineCategories,
    availableCuisineCategories,
    filters,
    loading,
    overflowDishFilters = false,
    showCuisineFilterAsDropdown = false,
    showFiltersInline = false,
  }: ExploreFiltersProps): JSX.Element => {
    const [isInitialCuisineLoad, setIsInitialCuisineLoad] = useState(true);
    const [persistedAvailableCuisines, setPersistedAvailableCuisines] = useState(availableCuisineCategories);
    const [optimisticFilters, setOptimisticFilters] = useState(filters);

    useEffect(() => {
      if (isInitialCuisineLoad && !loading) {
        setIsInitialCuisineLoad(false);
      }
    }, [loading, isInitialCuisineLoad]);

    useEffect(() => {
      // Don't update to an empty list if we already have a list
      if (!loading && (availableCuisineCategories.length > 0 || persistedAvailableCuisines.length === 0)) {
        setPersistedAvailableCuisines(availableCuisineCategories);
        setOptimisticFilters(filters);
      }
    }, [availableCuisineCategories, loading, persistedAvailableCuisines, filters]);

    const [topShefInfoModalSeen, setTopShefInfoModalSeen] = useLocalStorage<boolean>(
      StorageKeys.TOP_SHEF_INFO_MODAL_SEEN,
      false
    );

    const isTopShefModalShowing = !topShefInfoModalSeen && filters.tagFilters.includes(TagFilter.TopShef);
    const hideTopShefModal = useCallback(() => {
      setTopShefInfoModalSeen(true);
    }, []);

    const dishFilters = (
      <div
        className='no-scrollbar flex items-center gap-2 overflow-x-auto overflow-y-hidden pb-4'
        id={ExploreFilterIds.FilterButtonRow}>
        {showCuisineFilterAsDropdown && <CuisineFilterButton />}
        <SubCuisineFilterButton />
        <TopShefFilterButton className='shrink-0' />
        <TopShefInfoModal isShowing={isTopShefModalShowing} onClose={hideTopShefModal} />
        <TopShefFilterButtonDivider className='mx-1.5 shrink-0' />
        {/* divider */}
        <DietaryFilterButton />
        <MoreFiltersButton />
        {/* n.b. - This button only shows up when there are filters from the More Filters modal selected
        However, it clears all the filters. */}
        <ClearFiltersButton />
      </div>
    );
    return (
      <StyledExploreFilters showFiltersInline={showFiltersInline}>
        <ExploreFiltersContainer
          data-intercom-id='explore-filters'
          data-cy='explore-filters'
          showInline={showCuisineFilterAsDropdown}>
          <div id={ExploreFilterIds.DatePicker}>
            <DateFilterButton isLargeText />
          </div>
          {!showCuisineFilterAsDropdown && (
            <div id={ExploreFilterIds.CuisineCarousel}>
              <CuisineFilterCarousel
                allCuisineCategories={allCuisineCategories}
                availableCuisineCategories={persistedAvailableCuisines}
                filters={optimisticFilters}
                onFilterChange={setOptimisticFilters}
                loading={loading && isInitialCuisineLoad}
              />
            </div>
          )}
          {overflowDishFilters ? <div className='w-full overflow-x-auto'>{dishFilters}</div> : dishFilters}
        </ExploreFiltersContainer>
        <BrowseByMealType />
      </StyledExploreFilters>
    );
  }
);

ExploreFilters.displayName = 'ExploreFilters';
